import { Component, EventEmitter, Input, Output, QueryList, ViewChildren, AfterViewInit, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SliderComponent } from "../../ui/slider/slider.component";
import { AnswerOption, Survey } from '../../data/survey.model';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';

@Component({
  selector: 'app-survey-slider',
  imports: [CommonModule, SliderComponent, TranslatePipe],
  templateUrl: './survey-slider.component.html',
  styleUrl: './survey-slider.component.scss',
})
export class SurveySliderComponent implements OnInit, AfterViewInit {
  @Input() survey!: Survey;
  @Input() answerOptions!: AnswerOption[];

  @Output() backgroundChange = new EventEmitter<string>();
  @Output() surveyFinished = new EventEmitter<Survey>();
  @Output() progressChange = new EventEmitter<number>();

  @ViewChildren('sliders') sliders!: QueryList<SliderComponent>;

  isMobile = window.innerWidth < 768;
  activeSliderIndex = 0;
  lastActiveSliderIndex = 0;
  activeOptionIndex = -1;
  sliderClicked = false;
  private lastAnswerTime: number = Date.now();

  get activeQuestionIndex(): number {
    return Math.min(this.activeSliderIndex, this.survey.questions.length - 1);
  }

  ngOnInit(): void {
    if (this.survey.questions.every((question) => question.answer !== -1)) {
      this.activeSliderIndex = this.survey.questions.length;
      this.lastActiveSliderIndex = this.survey.questions.length;
      this.backgroundChange.emit(this.survey.questions[this.activeSliderIndex - 1].video);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.survey.questions.every((question) => question.answer !== -1)) {
        this.sliders.forEach((slider, index) => {
          const question = this.survey.questions[index];
          slider.setToTickIndex(this.answerOptions.length - question.answer - 1);
        });
      }
    }, 50);
  }

  onSliderChange(answerOptionIndex: number, index: number): void {
    this.sliderClicked = false;

    if (index === this.activeSliderIndex) {
      const isMobile = window.innerWidth < 768;

      if (isMobile) {
        setTimeout(() => {
          this.updateProgress(answerOptionIndex, index, isMobile);
        }, 600);

        return;
      }

      this.updateProgress(answerOptionIndex, index);
    }
  }

  updateProgress(answerOptionIndex: number, index: number, isMobile = false): void {
    this.setNewQuestion(answerOptionIndex, index, isMobile);

    const limit = isMobile
      ? this.survey.questions.length - 1
      : this.survey.questions.length;

    if (index >= this.lastActiveSliderIndex && this.lastActiveSliderIndex < limit) {
      this.lastActiveSliderIndex = index + 1;
    }

    if (this.survey.questions.every((question) => question.answer !== -1)) {
      this.progressChange.emit(100);
      this.surveyFinished.emit(this.survey);
    }
  }

  onTickIndexChange(index: number): void {
    this.activeOptionIndex = index;
  }

  onSliderClick(index: number): void {
    this.sliderClicked = true;

    if (index <= this.activeSliderIndex && index < this.survey.questions.length) {
      this.activeSliderIndex = index;
      this.progressChange.emit(this.activeSliderIndex * 100 / this.survey.questions.length);
      this.backgroundChange.emit(this.survey.questions[index].video);
    }
  }

  onCarouselDotClick(index: number): void {
    this.sliderClicked = false;
    this.activeSliderIndex = index;

    if (index < this.survey.questions.length) {
      this.backgroundChange.emit(this.survey.questions[index].video);
    }
  }

  private setNewQuestion(answerOptionIndex: number, index: number, isMobile = false): void {
    if (this.survey.questions[index].answer === -1) {
      const currentTime = Date.now();
      const timeTaken = currentTime - this.lastAnswerTime;
      this.lastAnswerTime = currentTime;
      this.survey.questions[index].timeTaken = timeTaken;
    }

    this.survey.questions[index].answer = answerOptionIndex;
    const nextIndex = this.survey.questions.findIndex((question) => question.answer === -1);
    const limit = isMobile ? this.survey.questions.length - 1 : this.survey.questions.length;
    this.activeSliderIndex = nextIndex !== -1 ? nextIndex : limit;

    setTimeout(() => {
      if (this.activeSliderIndex < limit) {
        this.backgroundChange.emit(this.survey.questions[this.activeSliderIndex].video);
      }
    }, 250);

    this.progressChange.emit(this.activeSliderIndex * 100 / this.survey.questions.length);
  }
}
